import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../home/components/Navbar";
import FooterSection from "../home/components/FooterSection";
import StickyMapButton from "../home/components/StickyMapButton";
import ChatbotWidget from "../home/components/ChatbotWidget";
import CategoryHeader from "./components/CategoryHeader";
import FilterBar, { type FilterState } from "./components/FilterBar";
import VenueCard from "./components/VenueCard";
import { allVenues, CATEGORY_META } from "../../mocks/allVenues";

const ITEMS_PER_PAGE = 12;

// Sponsored banner component
function SponsoredBanner() {
  const { t } = useTranslation();
  return (
    <div className="col-span-full bg-gradient-to-r from-forest-900 to-anthracite-800 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-5">
      <div className="w-12 h-12 rounded-2xl bg-forest-600 flex items-center justify-center shrink-0">
        <i className="ri-advertisement-line text-white text-xl" />
      </div>
      <div className="flex-1 text-center sm:text-left">
        <h4 className="font-display font-bold text-white text-base mb-1">
          {t("cat_page_sponsored_banner_title")}
        </h4>
        <p className="text-white/60 text-sm font-body">{t("cat_page_sponsored_banner_desc")}</p>
      </div>
      <Link
        to="/business"
        className="px-5 py-2.5 bg-forest-500 hover:bg-forest-400 text-white text-sm font-semibold font-body rounded-xl transition-colors cursor-pointer whitespace-nowrap shrink-0"
      >
        {t("cat_page_sponsored_banner_cta")}
      </Link>
    </div>
  );
}

export default function CategoryPage() {
  const { slug = "historical" } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const [filters, setFilters] = useState<FilterState>({
    districts: [],
    sort: "popular",
    openOnly: false,
    sponsoredOnly: false,
    editorOnly: false,
  });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // Filter + sort
  const allFiltered = useMemo(() => {
    const slugToUse = slug === "featured" ? undefined : slug;

    let result = slugToUse
      ? allVenues.filter((v) => v.categorySlug === slugToUse)
      : [...allVenues]; // featured = all

    if (slug === "featured") {
      result = result.filter((v) => v.isEditorPick || v.isSponsored);
    }

    if (filters.districts.length > 0) {
      result = result.filter((v) => filters.districts.includes(v.district));
    }
    if (filters.openOnly) result = result.filter((v) => v.isOpen);
    if (filters.sponsoredOnly) result = result.filter((v) => v.isSponsored);
    if (filters.editorOnly) result = result.filter((v) => v.isEditorPick);

    switch (filters.sort) {
      case "popular":
        result = [...result].sort((a, b) => b.reviews - a.reviews);
        break;
      case "rating":
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case "distance":
        result = [...result].sort((a, b) => a.distanceKm - b.distanceKm);
        break;
      case "newest":
        result = [...result].sort((a, b) => b.createdOrder - a.createdOrder);
        break;
    }

    return result;
  }, [slug, filters]);

  const visible = allFiltered.slice(0, visibleCount);
  const hasMore = visibleCount < allFiltered.length;

  const meta = CATEGORY_META[slug] ?? CATEGORY_META["featured"];

  return (
    <div className="min-h-screen bg-cream-50 font-body">
      <Navbar />
      <main>
        <CategoryHeader slug={slug} totalCount={allFiltered.length} />

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
          {/* Mobile filter button + sort row */}
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <button
              onClick={() => setMobileFilterOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-stone-200 rounded-xl text-sm font-semibold font-body text-anthracite-700 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-equalizer-line" />
              {t("cat_page_filter_title")}
              {(filters.districts.length > 0 || filters.openOnly || filters.sponsoredOnly || filters.editorOnly) && (
                <span className="w-2 h-2 rounded-full bg-forest-600" />
              )}
            </button>
            {/* Sort quick select (mobile) */}
            <select
              value={filters.sort}
              onChange={(e) => setFilters({ ...filters, sort: e.target.value as FilterState["sort"] })}
              className="flex-1 px-3 py-2.5 bg-white border border-stone-200 rounded-xl text-sm font-body text-anthracite-700 outline-none cursor-pointer"
            >
              <option value="popular">{t("cat_page_sort_popular")}</option>
              <option value="rating">{t("cat_page_sort_rating")}</option>
              <option value="distance">{t("cat_page_sort_distance")}</option>
              <option value="newest">{t("cat_page_sort_newest")}</option>
            </select>
          </div>

          {/* Main layout */}
          <div className="flex gap-6 items-start">
            <FilterBar
              filters={filters}
              onChange={setFilters}
              isMobileOpen={mobileFilterOpen}
              onMobileClose={() => setMobileFilterOpen(false)}
              totalCount={allFiltered.length}
              filteredCount={allFiltered.length}
            />

            {/* Venue grid */}
            <div className="flex-1 min-w-0">
              {/* Results info */}
              <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-anthracite-500 font-body">
                  <span className="font-semibold text-anthracite-800">{allFiltered.length}</span>{" "}
                  {t("cat_page_results")}
                </p>
                <div className="hidden lg:flex items-center gap-1.5 text-xs text-anthracite-400 font-body">
                  <i className="ri-layout-grid-line" />
                  <span>{meta.title}</span>
                </div>
              </div>

              {/* No results */}
              {allFiltered.length === 0 ? (
                <div className="bg-white border border-stone-100 rounded-2xl p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-search-line text-2xl text-stone-400" />
                  </div>
                  <h3 className="font-display font-bold text-anthracite-900 text-xl mb-2">
                    {t("cat_page_no_results")}
                  </h3>
                  <p className="text-anthracite-400 text-sm font-body mb-5">
                    {t("cat_page_no_results_desc")}
                  </p>
                  <button
                    onClick={() =>
                      setFilters({ districts: [], sort: "popular", openOnly: false, sponsoredOnly: false, editorOnly: false })
                    }
                    className="px-6 py-2.5 bg-forest-600 text-white text-sm font-semibold font-body rounded-xl hover:bg-forest-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t("cat_page_reset_filters")}
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {visible.map((venue, index) => (
                      <>
                        <VenueCard key={venue.id} venue={venue} />
                        {/* Sponsored banner after every 6th card */}
                        {(index + 1) % 6 === 0 && index + 1 < visible.length && (
                          <SponsoredBanner key={`banner-${index}`} />
                        )}
                      </>
                    ))}
                  </div>

                  {/* Load more */}
                  {hasMore && (
                    <div className="mt-8 text-center">
                      <button
                        onClick={() => setVisibleCount((c) => c + ITEMS_PER_PAGE)}
                        className="px-8 py-3 border border-stone-300 bg-white rounded-xl text-anthracite-700 font-semibold font-body text-sm hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                      >
                        {t("cat_page_load_more")}
                        <i className="ri-arrow-down-line" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <FooterSection />
      <StickyMapButton />
      <ChatbotWidget />
    </div>
  );
}
